/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewAttributeList.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ViewUploadedFile from "../../../components/file-upload/ViewUploadedFile";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import { componentStyle } from "../../../config/appTheme";

function getViewComponentBasedOnAttributeType({
  att,
  index,
  setSelectedFile,
  setSelectedFileName,
  setOpenPreview,
  onUpdateAttribute,
  onDeleteAttribute,
  onCopyAttribute,
  type,
  disableField,
}) {
  const handleCopyValue = async (value) => {
    const textToCopy =
      typeof value === "string"
        ? value
        : value?.filename || value?.name || value?.url || "";

    if (!textToCopy) return;

    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
      } catch (err) {
        console.error(err);
      }
    } else {
      console.warn("Clipboard API not supported");
    }
    if (onCopyAttribute) {
      onCopyAttribute(textToCopy);
    }
  };

  switch (att?.attribute_type) {
    case "TEXT":
    case "NUMBER":
      return (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                mb: 0.5,
                color: "#6f7174",
                ...componentStyle.font.form.textFieldLabel,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {att.attribute_heading}
            </Typography>
            {type === "COPY" && (
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => handleCopyValue(att?.attribute_value)}
                  aria-label="copy attribute value"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {type !== "COPY" && !disableField && (
              <IconButton
                size="small"
                onClick={() => onDeleteAttribute(index)}
                aria-label="delete attribute"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <CustomFormTextFieldV2
            size={"small"}
            required={true}
            type={att?.attribute_type?.toLowerCase()}
            fldType={"textR"}
            value={att?.attribute_value ?? ""}
            setValue={(value) => onUpdateAttribute(index, value)}
            disabled={disableField}
          />
        </Box>
      );

    case "FILE":
      return (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography>{att?.attribute_heading}</Typography>
            {!disableField && (
              <IconButton
                size="small"
                onClick={() => onDeleteAttribute(index)}
                aria-label="delete attribute"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Button
            size="small"
            variant="text"
            disabled={!att?.attribute_value}
            onClick={() => {
              setSelectedFile(att?.attribute_value);
              setSelectedFileName(att?.attribute_heading);
              setOpenPreview(true);
            }}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              p: 0,
              minWidth: 0,
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            View
          </Button>
        </Box>
      );

    default:
      return null;
  }
}

function ViewAttributeList({
  attributeList = [],
  onUpdateAttribute = () => {},
  onDeleteAttribute = () => {},
  onCopyAttribute = () => {},
  type = "EDIT",
  disableField = false,
}) {
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  if (!Array.isArray(attributeList) || attributeList.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: "200px",
          display: "flex",
          flexDirection: "column",
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1.5,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            No attributes added yet.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: "200px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            p: 1,
          }}
        >
          {attributeList.map((att, index) => {
            return (
              <Box
                key={`${att?.attribute_id ?? "new"}-${att?.attribute_heading ?? "attribute"}-${index}`}
              >
                {getViewComponentBasedOnAttributeType({
                  att,
                  index,
                  setSelectedFile,
                  setSelectedFileName,
                  setOpenPreview,
                  onUpdateAttribute,
                  onDeleteAttribute,
                  onCopyAttribute,
                  type,
                  disableField,
                })}
              </Box>
            );
          })}
        </Box>
      </Box>

      <ViewUploadedFile
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setSelectedFile(null);
        }}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        downloadFile={true}
        fileName={selectedFileName}
      />
    </>
  );
}


ViewAttributeList.propTypes = {
  onCopyAttribute: PropTypes.func,
  type: PropTypes.string,
  disableField: PropTypes.bool,
  attributeList: PropTypes.array,
  onUpdateAttribute: PropTypes.func,
  onDeleteAttribute: PropTypes.func,
};

export default ViewAttributeList;
